import { useState, useCallback, useContext, useEffect } from "react";
import { TableRow, TableCell } from "@mui/material";
import TableStyle from "styles/application/TableStyle";
import EMICheckbox from "components/Elements/EMICheckbox";
import { ModelContext } from "contexts/ModelDetailContext";
import { READONLY_ACCESS } from "constants/AccessTypes/AccessTypes";
import TabelRowImage from "components/Elements/TabelRowImage";
import ImageViewer from "components/Elements/ImageViewer";
import { getSiteAssetRegistersLookup } from "services/clients/sites/siteAssetRegisters";
import SiteAssetRegisterDropdown from "components/Elements/SiteAssetRegisterDropdown";

const AT = TableStyle();

const debounce = (func, delay) => {
	let timer;
	return function () {
		let self = this;
		let args = arguments;
		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(self, args);
		}, delay);
	};
};

function Row({
	x,
	assets,
	count,
	postStage,
	deleteStage,
	patchStage,
	pageChange,
	modelType,
	modelAccess,
	customCaption,
	setStages,
	siteId,
	fetchFromDropDwn,
	page,
	setPage,
	fetchTaskStages,
	showAssetReferences,
	handleCustomSort,
}) {
	const [openImage, setOPenImage] = useState(false);
	const [ImageToOpen, setImageToOpen] = useState(null);
	const [state, setStates] = useState({
		selectedAsset: {
			assetNumber: x.siteAssetRegisterAssetNumber,
			id: x.siteAssetRegisterID,
			reference: x.siteAssetRegisterReference,
		},
		selected: x.id !== null,
	});
	const [modelState] = useContext(ModelContext);
	const [searchCount, setSearchCount] = useState(null);
	const [updating, setUpdating] = useState(false);
	const setState = (da) => setStates((th) => ({ ...th, ...da }));
	useEffect(() => {
		if (x) {
			setStates((prev) => ({
				...prev,
				selectedAsset: {
					assetNumber: x.siteAssetRegisterAssetNumber,
					id: x.siteAssetRegisterID,
					reference: x.siteAssetRegisterReference,
				},
			}));
		}
	}, [x]);

	const patchAsset = async (asset) => {
		try {
			let res = await patchStage(x.id, asset);
			if (res.success) {
				return true;
			} else {
				// if failed to patch, it will set to previous asset
				setState({ selectedAsset: res.data });
			}
		} catch (e) {
			return;
		}
	};

	const failedResponse = (res) => {
		setState({ selected: res.data });
	};

	const postSelected = async () => {
		try {
			let res = await postStage({
				ModelVersionStageID: x.modelVersionStageID,
				SiteAssetRegisterID: state.selectedAsset.id,
			});
			if (res.success) {
				return true;
			} else {
				setState({ selected: !state.selected });
				failedResponse(res);
			}
		} catch (e) {
			return;
		}
	};

	const deleteSelected = async () => {
		try {
			let res = await deleteStage(x.id);
			if (res.success) {
				return true;
			} else {
				setState({ selected: !state.selected });
				failedResponse(res);
			}
		} catch (e) {
			return;
		}
	};

	const onAssetChange = (selectedAsset) => {
		setState({
			selectedAsset: selectedAsset,
		});
		patchAsset({
			...selectedAsset,
			siteAssetRegisterID: selectedAsset.id,
			name: selectedAsset.assetNumber || selectedAsset.name,
			assetReferenceName: selectedAsset.reference || selectedAsset.assetReferenceName,
		});
	};

	const handleAssetDropPage = async (p, prevData) => {
		setPage((prev) => ({ ...prev, pageNo: p }));
		await pageChange({ pNo: p, pSize: 10, search: "" }, prevData, page.sortField, page.sortOrder);
	};

	const handleSelected = async () => {
		setUpdating(true);
		const toggle = !state.selected;
		setState({ selected: toggle });
		if (toggle) {
			await postSelected();
		} else {
			await deleteSelected();
		}
		setUpdating(false);
	};

	const handleServerSideSearch = useCallback(
		debounce(async (searchTxt) => {
			if (searchTxt) {
				const response = await getSiteAssetRegistersLookup(
					null,
					siteId,
					1,
					100,
					searchTxt
				);

				if (response.status) {
					setStages((prev) => ({ ...prev, assets: response.data }));
				}
				setSearchCount(1);
			} else {
				setSearchCount(null);
				handleAssetDropPage(1, assets);
			}
		}, 500),
		[]
	);

	return (
		<>
			<ImageViewer
				open={openImage}
				onClose={() => {
					setOPenImage(false);
					setImageToOpen(null);
				}}
				imgSource={ImageToOpen}
			/>
			<TableRow>
				<TableCell
					style={{
						// display: "flex",
						// alignItems: "center",
						// justifyContent: "center",
						height: modelType !== "F" ? "100%" : 113,
					}}
				>
					<EMICheckbox
						state={state.selected}
						changeHandler={handleSelected}
						disabled={
							modelAccess === READONLY_ACCESS ||
							modelState?.modelDetail?.isPublished ||
							updating
						}
					/>
				</TableCell>
				<TableCell>
					<AT.CellContainer>
						<AT.TableBodyText>{x.name}</AT.TableBodyText>
					</AT.CellContainer>
				</TableCell>
				{(modelType === "F" || modelType === "A") && x?.thumbnailURL ? (
					<TableCell>
						<TabelRowImage
							imageURL={x?.thumbnailURL}
							imageHeight={"100px"}
							imageWidth={"100px"}
							onClickImage={() => {
								setOPenImage(true);
								setImageToOpen(x?.imageURL);
							}}
						/>
					</TableCell>
				) : (
					<AT.CellContainer>&nbsp;</AT.CellContainer>
				)}
				{modelType === "F" && (
					<TableCell>
						<SiteAssetRegisterDropdown
							dataSource={assets}
							selectedValue={state.selectedAsset?.id ? state.selectedAsset : null}
							onChange={onAssetChange}
							count={searchCount || count}
							onPageChange={handleAssetDropPage}
							page={page}
							handleServerSideSearch={handleServerSideSearch}
							disabled={!state.selected}
							isReadOnly={
								modelAccess === READONLY_ACCESS ||
								modelState?.modelDetail?.isPublished
							}
							customCaptions={customCaption}
							fetchData={() =>
								fetchFromDropDwn(
									x.defaultSiteAssetFilter !== null
										? x.defaultSiteAssetFilter
										: ""
								)
							}
							preloadedSearch={x.defaultSiteAssetFilter}
							showReference={showAssetReferences}
							handleCustomSort={handleCustomSort}
							cacheDropDownData={false}
						/>
					</TableCell>
				)}
			</TableRow>
		</>
	);
}

export default Row;
